import { useQuery, UseQueryResult } from '@tanstack/react-query'
import { v4 as uuidv4 } from 'uuid'
import { labelSets } from '@opallabs/api/v2'
import { queryKeys } from './queryKeys'
import { FilterOption, isFilterOption } from './types'

const labelCategoryUuid = uuidv4()

export const useLabelFilters = ({
  enabled,
  workspaceId,
}: {
  enabled: boolean
  workspaceId: number
}): UseQueryResult<FilterOption | null> =>
  useQuery({
    queryKey: queryKeys.labelSetFilters(workspaceId),
    queryFn: () =>
      labelSets.readCollection({
        query: {
          include: ['labels'],
          page: { limit: 1000 },
          filter: { brand_id: [workspaceId] },
        },
      }),
    enabled,
    select: (data): FilterOption | null => {
      if (data.data === undefined) return null

      // Only build out label sets that have labels
      const labelSets = data.data.filter(
        (labelSet) =>
          Array.isArray(labelSet?.relationships?.labels?.data) &&
          labelSet.relationships &&
          labelSet.relationships.labels.data.length > 0
      )

      if (!Array.isArray(labelSets) || labelSets.length === 0) {
        return null
      }

      const includedLabels = data?.included?.filter(
        (include) => include.type === 'label'
      )
      if (!includedLabels) return null

      return {
        divider: false,
        groupOrAble: false,
        id: `label-set-${labelCategoryUuid}`,
        uuid: labelCategoryUuid,
        inclusivity: false,
        title: 'Labels',
        icon: 'gem-label-set',
        childOptions: labelSets
          .map<FilterOption | null>(
            ({ id: labelSetId, attributes, relationships }) => {
              const labelRelationships =
                relationships && relationships.labels.data

              if (labelRelationships === undefined) return null

              return {
                divider: false,
                groupOrAble: false,
                icon: 'gem-label-set',
                id: `label-set-${labelSetId}`,
                uuid: attributes.uuid,
                inclusivity: false,
                title: attributes?.name,
                childOptions: labelRelationships
                  .map<FilterOption | null>(({ id: labelId }) => {
                    const labelData = includedLabels.find(
                      (l) => l.id === labelId
                    )
                    if (labelData === undefined) return null

                    return {
                      color: labelData?.attributes?.color,
                      divider: false,
                      groupOrAble: false,
                      icon: 'gem-label',
                      id: `label-${labelData?.id}`,
                      uuid: labelData.attributes.uuid,
                      inclusivity: false,
                      title: labelData?.attributes?.name,
                      payload: {
                        type_key: 'labels',
                        object: {
                          id:
                            typeof labelId === 'string'
                              ? parseInt(labelId, 10)
                              : labelId,
                          label_set_id:
                            typeof labelSetId === 'string'
                              ? parseInt(labelSetId, 10)
                              : labelSetId,
                        },
                      },
                    }
                  })
                  .filter<FilterOption>(isFilterOption),
              }
            }
          )
          .filter<FilterOption>(isFilterOption),
      }
    },
  })
